import { useState, useEffect } from 'react'
import api from '../services/api'

// Certificados e Registros de funcionário (TVCTCAD, frota.vctcad com vctesp = 'F').
// A tabela também guarda os de veículo (V) e de empresa (E), que não aparecem aqui.

interface Certificado { vctcod: number; vctdes: string; vctati: string; funcionarios?: number }

function Lista({ lista, onAdicionar, onEditar, onExcluir }: {
  lista: Certificado[]
  onAdicionar: () => void
  onEditar: (c: Certificado) => void
  onExcluir: (c: Certificado) => void
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>Cadastrados ({lista.length})</h3>
        <button className="btn btn-primary" onClick={onAdicionar}>+ Adicionar</button>
      </div>

      {lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhum certificado ou registro cadastrado.</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {lista.map(c => (
          <div key={c.vctcod} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, opacity: c.vctati === 'I' ? 0.6 : 1 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{c.vctcod} - {c.vctdes}</div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <span>{c.vctati === 'I' ? 'Inativo' : 'Ativo'}</span>
                <span>Funcionários: <b>{c.funcionarios ?? 0}</b></span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              <button className="btn btn-outline btn-sm" onClick={() => onEditar(c)}>Editar</button>
              <button className="btn btn-outline btn-sm" style={{ color: '#b91c1c' }} onClick={() => onExcluir(c)}
                disabled={(c.funcionarios ?? 0) > 0} title={(c.funcionarios ?? 0) > 0 ? 'Usado em funcionários — marque como Inativo' : undefined}>Excluir</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function Formulario({ editando, inicial, onVoltar, onSalvo }: {
  editando: boolean
  inicial: Certificado
  onVoltar: () => void
  onSalvo: () => void
}) {
  const [form, setForm] = useState<Certificado>(inicial)
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (!form.vctcod || form.vctcod <= 0) { setErro('Codigo invalido!'); return }
    if (!form.vctdes.trim()) { setErro('Informe a descricao!'); return }
    setSalvando(true); setErro('')
    try {
      if (editando) await api.put(`/certificados-registros/${form.vctcod}`, form)
      else await api.post('/certificados-registros', form)
      onSalvo()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao salvar.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>{editando ? 'Editar Certificado / Registro' : 'Novo Certificado / Registro'}</h3>
        <button className="btn btn-outline" onClick={onVoltar}>Voltar</button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="field">
          <label>Código *</label>
          <input type="number" value={form.vctcod || ''} disabled={editando} style={{ width: '10ch' }}
            onChange={e => setForm(f => ({ ...f, vctcod: Number(e.target.value) }))} />
        </div>
        <div className="field">
          <label>Descrição *</label>
          <input value={form.vctdes} maxLength={40} style={{ width: '42ch' }}
            onChange={e => setForm(f => ({ ...f, vctdes: e.target.value.toUpperCase() }))} />
        </div>
        <div className="field">
          <label>Ativo/Inativo</label>
          <select value={form.vctati} onChange={e => setForm(f => ({ ...f, vctati: e.target.value }))}>
            <option value="A">Ativo</option>
            <option value="I">Inativo</option>
          </select>
        </div>
      </div>

      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
        <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Adicionar'}
        </button>
        <button className="btn btn-outline" onClick={onVoltar}>Cancelar</button>
      </div>
    </div>
  )
}

export default function CertificadosRegistros() {
  const [lista, setLista] = useState<Certificado[]>([])
  const [proximo, setProximo] = useState(1)
  const [carregado, setCarregado] = useState(false)
  const [atual, setAtual] = useState<Certificado | null>(null)
  const [editando, setEditando] = useState(false)
  const [erro, setErro] = useState('')

  async function carregar() {
    const r = await api.get('/certificados-registros')
    setLista(r.data.lista)
    setProximo(r.data.proximo)
    setCarregado(true)
  }

  useEffect(() => { carregar().catch(() => setErro('Erro ao carregar os certificados.')) }, [])

  async function excluir(c: Certificado) {
    if (!window.confirm(`Excluir "${c.vctcod} - ${c.vctdes}"?`)) return
    setErro('')
    try {
      await api.delete(`/certificados-registros/${c.vctcod}`)
      await carregar()
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao excluir.')
    }
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Certificados e Registros
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.vctcad, tipo F (funcionário)">TVCTCAD</span>
      </h2>
      {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{erro}</div>}

      {!carregado ? null : atual ? (
        <Formulario key={atual.vctcod || 'novo'} editando={editando} inicial={atual}
          onVoltar={() => setAtual(null)} onSalvo={async () => { await carregar(); setAtual(null) }} />
      ) : (
        <Lista lista={lista}
          onAdicionar={() => { setAtual({ vctcod: proximo, vctdes: '', vctati: 'A' }); setEditando(false) }}
          onEditar={c => { setAtual({ ...c }); setEditando(true) }}
          onExcluir={excluir} />
      )}
    </div>
  )
}
